import { useState, useMemo } from 'react'
import { usePublishHistory, useCampaignDetail, type PublishHistoryItem } from '@/api/hooks/usePoster'
import { useLanguage } from '@/i18n/LanguageContext'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  History, Video, ImageIcon, CalendarDays, TrendingUp, Users, ChevronDown, ChevronRight,
  Filter, Play, Rss, BookImage, Loader2,
} from 'lucide-react'

const fmtTime = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' })

function startOfDay(ts: number) {
  const d = new Date(ts)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

function getDateGroup(ts: number, t: (s: string) => string): string {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const yesterday = today - 86400000
  const weekAgo = today - 7 * 86400000
  const dayTs = startOfDay(ts)

  if (dayTs >= today) return t('Hoje')
  if (dayTs >= yesterday) return t('Ontem')
  if (dayTs >= weekAgo) return t('Esta Semana')
  return t('Anteriores')
}

function TypeBadge({ item, t }: { item: PublishHistoryItem; t: (s: string) => string }) {
  const isVideo = item.videoPosts > 0 && item.imagePosts === 0
  const isImage = item.imagePosts > 0 && item.videoPosts === 0

  if (isVideo) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-blue-500/10 px-2 py-0.5 text-[10px] font-semibold text-blue-600 dark:text-blue-400 w-[72px] justify-center">
        <Video className="h-3 w-3" /> {t('Videos')}
      </span>
    )
  }
  if (isImage) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 w-[72px] justify-center">
        <ImageIcon className="h-3 w-3" /> {t('Imagens')}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground w-[72px] justify-center">
      {t('Misto')}
    </span>
  )
}

function CampaignMediaGrid({ campaignId }: { campaignId: string }) {
  const { t } = useLanguage()
  const { data: posts = [], isLoading } = useCampaignDetail(campaignId)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (posts.length === 0) {
    return (
      <div className="py-4 text-center text-xs text-muted-foreground">
        {t('Nenhuma midia encontrada')}
      </div>
    )
  }

  return (
    <div className="p-3 space-y-3">
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2">
        {posts.map(post => (
          <div key={post.id} className="group relative overflow-hidden rounded-lg border border-border bg-muted/30">
            {/* Thumbnail */}
            <div className="relative aspect-square">
              {post.mediaType === 'video' ? (
                <>
                  <video
                    src={post.imageUrl}
                    className="h-full w-full object-cover"
                    muted
                    preload="metadata"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Play className="h-5 w-5 fill-white text-white opacity-70" />
                  </div>
                </>
              ) : (
                <img
                  src={post.imageUrl}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              )}

              {/* Type indicator */}
              <div className="absolute top-1 left-1">
                <Badge className="border-none bg-black/70 text-[8px] text-white px-1 py-0">
                  {post.postType === 'story' ? 'Story' : post.mediaType === 'video' ? 'Video' : 'Img'}
                </Badge>
              </div>
            </div>

            {/* Info */}
            <div className="px-1.5 py-1">
              <div className="flex items-center gap-1">
                {post.accounts.slice(0, 2).map(acct => (
                  <span key={acct} className="text-[8px] text-muted-foreground truncate max-w-[60px]">{acct}</span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className="flex items-center gap-3 text-[11px] text-muted-foreground pt-1 border-t border-border">
        <span>{posts.length} {posts.length !== 1 ? t('midias unicas') : t('midia')}</span>
        {posts.some(p => p.accounts.length > 1) && (
          <span>&middot; {t('Publicado em multiplas contas')}</span>
        )}
      </div>
    </div>
  )
}

export function PublishHistory() {
  const { t } = useLanguage()
  const { data: history = [], isLoading } = usePublishHistory()
  const [accountFilter, setAccountFilter] = useState<string>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<'all' | 'video' | 'image'>('all')
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null)

  // Extract unique accounts and categories
  const allAccounts = useMemo(() => {
    const set = new Set<string>()
    history.forEach(item => item.accounts.forEach(a => set.add(a)))
    return Array.from(set).sort()
  }, [history])

  const allCategories = useMemo(() => {
    const set = new Set<string>()
    history.forEach(item => set.add(item.category))
    return Array.from(set).sort()
  }, [history])

  // Filter
  const filtered = useMemo(() => {
    return history.filter(item => {
      if (accountFilter !== 'all' && !item.accounts.includes(accountFilter)) return false
      if (categoryFilter !== 'all' && item.category !== categoryFilter) return false
      if (typeFilter === 'video' && item.videoPosts === 0) return false
      if (typeFilter === 'image' && item.imagePosts === 0) return false
      return true
    })
  }, [history, accountFilter, categoryFilter, typeFilter])

  // KPIs
  const kpis = useMemo(() => {
    const now = new Date()
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
    const weekStart = todayStart - 7 * 86400000

    const todayPosts = filtered
      .filter(i => i.publishedAt >= todayStart)
      .reduce((sum, i) => sum + i.totalPosts, 0)

    const weekPosts = filtered
      .filter(i => i.publishedAt >= weekStart)
      .reduce((sum, i) => sum + i.totalPosts, 0)

    const totalCampaigns = filtered.length

    const activeAccounts = new Set<string>()
    filtered.forEach(item => item.accounts.forEach(a => activeAccounts.add(a)))

    return { todayPosts, weekPosts, totalCampaigns, activeAccounts: activeAccounts.size }
  }, [filtered])

  // Group by date
  const groups = useMemo(() => {
    const order = [t('Hoje'), t('Ontem'), t('Esta Semana'), t('Anteriores')]
    const map = new Map<string, PublishHistoryItem[]>()

    filtered.forEach(item => {
      const group = getDateGroup(item.publishedAt, t)
      if (!map.has(group)) map.set(group, [])
      map.get(group)!.push(item)
    })

    return order
      .filter(key => map.has(key))
      .map(key => ({ label: key, items: map.get(key)! }))
  }, [filtered, t])

  const toggleGroup = (label: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      if (next.has(label)) next.delete(label)
      else next.add(label)
      return next
    })
  }

  const toggleCampaign = (id: string) => {
    setExpandedCampaign(prev => prev === id ? null : id)
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="p-3 animate-pulse">
              <div className="h-4 bg-muted rounded w-1/2 mb-2" />
              <div className="h-6 bg-muted rounded w-1/3" />
            </Card>
          ))}
        </div>
        <Card className="p-4 animate-pulse">
          <div className="h-4 bg-muted rounded w-1/4 mb-3" />
          <div className="space-y-2">
            {[1, 2, 3].map(i => <div key={i} className="h-8 bg-muted rounded" />)}
          </div>
        </Card>
      </div>
    )
  }

  if (history.length === 0) {
    return (
      <Card className="p-12 text-center">
        <div className="flex justify-center mb-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
            <History className="h-8 w-8 text-muted-foreground" />
          </div>
        </div>
        <h2 className="text-lg font-semibold mb-1">{t('Nenhuma publicacao')}</h2>
        <p className="text-sm text-muted-foreground">
          {t('O historico aparece automaticamente quando campanhas sao publicadas.')}
        </p>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* KPI Cards */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="p-3 flex items-center gap-3">
          <CalendarDays className="h-5 w-5 text-primary" />
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{t('Hoje')}</div>
            <div className="text-lg font-bold">{kpis.todayPosts} <span className="text-xs font-normal text-muted-foreground">posts</span></div>
          </div>
        </Card>
        <Card className="p-3 flex items-center gap-3">
          <TrendingUp className="h-5 w-5 text-blue-500" />
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{t('Semana')}</div>
            <div className="text-lg font-bold">{kpis.weekPosts} <span className="text-xs font-normal text-muted-foreground">posts</span></div>
          </div>
        </Card>
        <Card className="p-3 flex items-center gap-3">
          <History className="h-5 w-5 text-emerald-500" />
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{t('Campanhas')}</div>
            <div className="text-lg font-bold">{kpis.totalCampaigns}</div>
          </div>
        </Card>
        <Card className="p-3 flex items-center gap-3">
          <Users className="h-5 w-5 text-purple-500" />
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{t('Contas Ativas')}</div>
            <div className="text-lg font-bold">{kpis.activeAccounts}</div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground shrink-0" />

        <select
          value={accountFilter}
          onChange={e => setAccountFilter(e.target.value)}
          className="h-8 rounded-md border border-input bg-background text-foreground px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="all">{t('Todas as contas')}</option>
          {allAccounts.map(acct => (
            <option key={acct} value={acct}>{acct}</option>
          ))}
        </select>

        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          className="h-8 rounded-md border border-input bg-background text-foreground px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="all">{t('Todas as categorias')}</option>
          {allCategories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>

        <div className="flex gap-0.5 rounded-md bg-muted p-0.5">
          {(['all', 'video', 'image'] as const).map(type => (
            <button
              key={type}
              onClick={() => setTypeFilter(type)}
              className={cn(
                'flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded transition-colors',
                typeFilter === type
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {type === 'all' && t('Todos')}
              {type === 'video' && <><Video className="h-3 w-3" /> {t('Videos')}</>}
              {type === 'image' && <><ImageIcon className="h-3 w-3" /> {t('Imagens')}</>}
            </button>
          ))}
        </div>

        {(accountFilter !== 'all' || categoryFilter !== 'all' || typeFilter !== 'all') && (
          <button
            onClick={() => { setAccountFilter('all'); setCategoryFilter('all'); setTypeFilter('all') }}
            className="text-[11px] text-muted-foreground hover:text-foreground underline"
          >
            {t('Limpar filtros')}
          </button>
        )}
      </div>

      {/* Grouped list */}
      {filtered.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('Nenhum resultado com os filtros selecionados.')}</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {groups.map(group => {
            const isCollapsed = collapsedGroups.has(group.label)
            const groupPostCount = group.items.reduce((sum, i) => sum + i.totalPosts, 0)

            return (
              <Card key={group.label} className="overflow-hidden">
                {/* Group header */}
                <button
                  onClick={() => toggleGroup(group.label)}
                  className="flex items-center gap-2 w-full px-4 py-2.5 bg-muted/40 hover:bg-muted/60 transition-colors text-left"
                >
                  {isCollapsed
                    ? <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  }
                  <span className="text-xs font-semibold flex-1">{group.label}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {group.items.length} {group.items.length !== 1 ? t('campanhas') : t('campanha')} &middot; {groupPostCount} posts
                  </span>
                </button>

                {/* Rows */}
                {!isCollapsed && (
                  <div className="divide-y divide-border">
                    {group.items.map(item => {
                      const isExpanded = expandedCampaign === item.campaignId

                      return (
                        <div key={item.campaignId}>
                          <div
                            onClick={() => toggleCampaign(item.campaignId)}
                            className={cn(
                              'flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors',
                              isExpanded ? 'bg-primary/5' : 'hover:bg-muted/20',
                            )}
                          >
                            {/* Expand indicator */}
                            {isExpanded
                              ? <ChevronDown className="h-3 w-3 text-primary shrink-0" />
                              : <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                            }

                            {/* Type badge */}
                            <TypeBadge item={item} t={t} />

                            {/* Category name */}
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium truncate">{item.category}</p>
                            </div>

                            {/* Post breakdown */}
                            <div className="hidden sm:flex items-center gap-2 text-[11px] text-muted-foreground shrink-0">
                              {item.feedPosts > 0 && (
                                <span className="inline-flex items-center gap-0.5">
                                  <Rss className="h-2.5 w-2.5" /> {item.feedPosts}
                                </span>
                              )}
                              {item.storyPosts > 0 && (
                                <span className="inline-flex items-center gap-0.5">
                                  <BookImage className="h-2.5 w-2.5" /> {item.storyPosts}
                                </span>
                              )}
                            </div>

                            {/* Accounts */}
                            <div className="hidden md:flex items-center gap-1 shrink-0 max-w-[200px] overflow-hidden">
                              {item.accounts.slice(0, 3).map(acct => (
                                <span key={acct} className="text-[10px] text-muted-foreground bg-muted rounded px-1.5 py-0.5 truncate max-w-[80px]">
                                  {acct}
                                </span>
                              ))}
                              {item.accounts.length > 3 && (
                                <span className="text-[10px] text-muted-foreground">+{item.accounts.length - 3}</span>
                              )}
                            </div>

                            {/* Total + time */}
                            <div className="text-right shrink-0 w-[60px]">
                              <div className="text-xs font-semibold">{item.totalPosts}</div>
                              <div className="text-[10px] text-muted-foreground">{fmtTime.format(item.publishedAt)}</div>
                            </div>
                          </div>

                          {/* Expanded media grid */}
                          {isExpanded && (
                            <div className="border-t border-border bg-muted/10">
                              <CampaignMediaGrid campaignId={item.campaignId} />
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
